import { useMemo } from 'react';
import get from 'lodash/get';
import { useSSRQuery } from '../../../graphql';
import FLIGHTS_QUERY from './flights.query.graphql.js';
import { DEPARTING, RETURNING, tripTypeValues } from '../constants';
import useLowFareCalendarQueries from './useLowFareCalendarQueries';
import useDynamicSearchCriteria from './useDynamicSearchCriteria';
import useTransactionId from './useTransactionId';
import useFlightsQueryLoadingStatus from './useFlightsQueryLoadingStatus';
import tracking from "@g4/g4-analytics";

const useFlightsQuery = (flightSearchCriteria, tabDates) => {
  // useDynamicSearchCriteria controls the state and query-param cache used by ApolloClient for the flights-query.
  const searchCriteria = useDynamicSearchCriteria(
    flightSearchCriteria,
    tabDates
  );
  const handleTransactionId = useTransactionId();
  const dataLayerVars = tracking.getDataLayerVars();
  const googleAnalyticsClientId = dataLayerVars.customer_id;

  const { networkStatus, ...flightsQuery } = useSSRQuery(FLIGHTS_QUERY, {
    variables: searchCriteria,
    notifyOnNetworkStatusChange: true,
    onCompleted: handleTransactionId,
    context: {
      headers: {
        'google-analytics-client-id': googleAnalyticsClientId,
      }
    }
  });

  const {
    isFetchingDeparting,
    isFetchingReturning,
  } = useFlightsQueryLoadingStatus(tabDates, networkStatus);

  return {
    isFetchingDeparting,
    isFetchingReturning,
    ...flightsQuery,
  };
};

const useFlightsQueries = (flightSearchCriteria, tabDates) => {
  // query: contains the query execution results like loading, networkStatus, error, ...
  const {
    isFetchingDeparting,
    isFetchingReturning,
    ...flightsQuery
  } = useFlightsQuery(flightSearchCriteria, tabDates);

  const {
    [DEPARTING]: departingLowFareQuery,
    [RETURNING]: returningLowFareQuery,
  } = useLowFareCalendarQueries(flightSearchCriteria, tabDates);

  return {
    isOnewayWithRoundTripDiscount:
      flightSearchCriteria.tripType === tripTypeValues.ONEWAY &&
      !!get(flightsQuery, `data.flights.${DEPARTING}[0].incentiveReturnDate`),
    onewayToRoundtripSavedAmount: get(
      flightsQuery,
      `data.flights.${DEPARTING}[0].incentiveSavedAmount`
    ),
    roundtripDiscountValue: get(
      flightsQuery,
      `data.flights.${DEPARTING}[0].totalDiscountValue`
    ),
    [DEPARTING]: useMemo(
      () => ({
        flights: get(flightsQuery, `data.flights.${DEPARTING}`) || [],
        loading: isFetchingDeparting || departingLowFareQuery.loading,
        originAirport: get(
          flightsQuery,
          `data.flights.${DEPARTING}.[0].flight.origin.displayName`
        ),
        destinationAirport: get(
          flightsQuery,
          `data.flights.${DEPARTING}.[0].flight.destination.displayName`
        ),
        lowFareQuery: {
          loading: departingLowFareQuery.loading,
          error: departingLowFareQuery.error,
          lowFares:
            get(departingLowFareQuery, `data.lowFareFlightCalendar`) || [],
        },
      }),
      [flightsQuery, departingLowFareQuery, isFetchingDeparting]
    ),
    [RETURNING]: useMemo(
      () => ({
        flights: get(flightsQuery, `data.flights.${RETURNING}`) || [],
        loading: isFetchingReturning || returningLowFareQuery.loading,
        originAirport: get(
          flightsQuery,
          `data.flights.${RETURNING}.[0].flight.origin.displayName`
        ),
        destinationAirport: get(
          flightsQuery,
          `data.flights.${RETURNING}.[0].flight.destination.displayName`
        ),
        lowFareQuery: {
          loading: returningLowFareQuery.loading,
          error: returningLowFareQuery.error,
          lowFares:
            get(returningLowFareQuery, `data.lowFareFlightCalendar`) || [],
        },
      }),
      [flightsQuery, returningLowFareQuery, isFetchingReturning]
    ),
    flightsQueryError: flightsQuery.error,
    loyaltyFare: flightsQuery?.data?.flights?.loyaltyFare,
    ...flightsQuery,
  };
};

export default useFlightsQueries;
